import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Calendar } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import CommentSection from '../../commentSection';
import { Comment, DatabaseComment } from '../../../../types/types';

/**
 * Interface representing the props for the AnswerView component.
 *
 * - text The content of the answer.
 * - ansBy The username of the user who wrote the answer.
 * - meta Additional metadata related to the answer.
 * - comments An array of comments associated with the answer.
 * - handleAddComment Callback function to handle adding a new comment.
 */
interface AnswerProps {
  text: string;
  ansBy: string;
  meta: string;
  comments: DatabaseComment[];
  handleAddComment: (comment: Comment) => void;
}

/**
 * AnswerView component that displays the content of an answer with the author's name and metadata.
 * The answer text is processed to handle hyperlinks, and a comment section is included.
 *
 * @param text The content of the answer.
 * @param ansBy The username of the answer's author.
 * @param meta Additional metadata related to the answer.
 * @param comments An array of comments associated with the answer.
 * @param handleAddComment Function to handle adding a new comment.
 */
const AnswerView = ({ text, ansBy, meta, comments, handleAddComment }: AnswerProps) => (
  <div className='p-4'>
    <div className='flex items-start gap-3'>
      <div className='h-10 w-10 rounded-full bg-green-100 flex items-center justify-center text-green-600'>
        <MessageCircle size={18} />
      </div>

      <div className='flex-1'>
        <div className='flex flex-wrap justify-between gap-2 mb-1'>
          <Link to={`/user/${ansBy}`} className='font-medium text-blue-600 hover:underline'>
            {ansBy}
          </Link>

          <div className='flex items-center text-sm text-muted-foreground'>
            <Calendar size={14} className='mr-1' />
            {meta}
          </div>
        </div>

        <div className='mb-2'>
          <span className='text-sm text-muted-foreground'>answered the question</span>
        </div>

        <div className='mt-2 p-3 border-l-2 border-green-200 bg-green-50 rounded'>
          <div className='prose prose-sm max-w-none'>
            <ReactMarkdown>{text}</ReactMarkdown>
          </div>
        </div>

        <div className='mt-4'>
          <CommentSection comments={comments} handleAddComment={handleAddComment} />
        </div>
      </div>
    </div>
  </div>
);

export default AnswerView;
